/**
 * PersonalizarPainel — SlideOverPanel para escolher quais widgets
 * o usuario quer ver no dashboard.
 *
 * - Lista todos os widgets do catalogo que o usuario tem PERMISSAO
 *   (gated em `filtrarPorPermissoes`)
 * - Agrupado por categoria
 * - Busca de texto
 * - Toggle on/off persistido via onToggle (parent salva backend)
 *
 * O drag-and-drop pra reordenar acontece no GridLayout (fora deste
 * painel); aqui so escolhe quais widgets aparecem.
 */
import React, { useMemo, useState } from "react";

import SlideOverPanel from "../../components/SlideOverPanel";

import { filtrarPorPermissoes } from "./_catalogo";


export default function PersonalizarPainel({
  aberto,
  onFechar,
  idsAtivos,           // Set<string>
  isMaster,
  hasAllPermissions,
  onToggle,            // (widgetId, novoAtivo) => void
}) {
  const [busca, setBusca] = useState("");

  const disponiveis = useMemo(
    () => filtrarPorPermissoes(hasAllPermissions, isMaster),
    [hasAllPermissions, isMaster],
  );

  const filtrados = useMemo(() => {
    const q = busca.trim().toLowerCase();
    if (!q) return disponiveis;
    return disponiveis.filter(
      (w) =>
        (w.label || "").toLowerCase().includes(q) ||
        (w.descricao || "").toLowerCase().includes(q) ||
        (w.categoria || "").toLowerCase().includes(q),
    );
  }, [disponiveis, busca]);

  // Agrupa por categoria preservando ordem original
  const grupos = useMemo(() => {
    const mapa = new Map();
    filtrados.forEach((w) => {
      const cat = w.categoria || "Outros";
      if (!mapa.has(cat)) mapa.set(cat, []);
      mapa.get(cat).push(w);
    });
    return Array.from(mapa.entries());
  }, [filtrados]);

  return (
    <SlideOverPanel
      open={aberto}
      onClose={onFechar}
      title="Personalizar painel"
      subtitle="Escolha os indicadores que você quer ver."
      width="min(95vw, 480px)"
    >
      <div style={{ marginBottom: 12 }}>
        <input
          type="text"
          value={busca}
          onChange={(e) => setBusca(e.target.value)}
          placeholder="Buscar widget..."
          style={{
            width: "100%",
            padding: "8px 12px",
            borderRadius: 6,
            border: "1px solid var(--border)",
            fontSize: "0.9rem",
          }}
        />
      </div>

      {grupos.length === 0 ? (
        <p style={{ opacity: 0.7, fontSize: "0.85rem" }}>
          Nenhum widget encontrado.
          {disponiveis.length === 0 &&
            " Sua permissão atual não libera widgets — fale com o gestor."}
        </p>
      ) : (
        grupos.map(([categoria, widgets]) => (
          <section key={categoria} style={{ marginBottom: 16 }}>
            <h4
              style={{
                margin: "0 0 8px 0",
                fontSize: "0.72rem",
                textTransform: "uppercase",
                letterSpacing: "0.05em",
                opacity: 0.7,
              }}
            >
              {categoria}
            </h4>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {widgets.map((w) => {
                const ativo = idsAtivos.has(w.id);
                return (
                  <li
                    key={w.id}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      padding: "8px 0",
                      borderBottom: "1px solid var(--border)",
                    }}
                  >
                    <input
                      type="checkbox"
                      checked={ativo}
                      onChange={(e) => onToggle(w.id, e.target.checked)}
                      style={{ cursor: "pointer" }}
                      aria-label={`Ativar ${w.label}`}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontSize: "0.9rem", fontWeight: 600 }}>
                        {w.label}
                      </div>
                      {w.descricao && (
                        <div style={{ fontSize: "0.78rem", opacity: 0.7 }}>
                          {w.descricao}
                        </div>
                      )}
                    </div>
                  </li>
                );
              })}
            </ul>
          </section>
        ))
      )}
    </SlideOverPanel>
  );
}
